/**
 * ClientVersionReview: the Review tab's client versions (SafeShare slice 4.5,
 * admin only), above the guest models. A client job's models and previews
 * wait here for Ivin's approval before any client can reach them (the portal,
 * a preview link); each approval is for the exact version on screen.
 *
 * Rows come from listVersionReview (functions/src/zelma/services/versionReview.ts):
 * warnings first (a failed check, a deletion asked, a newer version already
 * shown). Per version: Look (any version's files, models too, signed for 10
 * minutes by lookVersion and opened in the Suite's viewer, ModelLook.jsx;
 * looking changes nothing), Approve (a preview only after Look), Hold,
 * Process again (a mark: nothing
 * the client sees changes), and
 * Remove (asks first; an approved one has its approval taken back and the
 * client sees the version before it, if one is approved). "Approve all that
 * passed" takes only versions to review whose reported checks all passed, and
 * asks once more. For a guest job the server answers that it is not a client
 * job and nothing is shown.
 */
import React, { useCallback, useEffect, useState } from "react";
import { rigv2 } from "./rigv2Callables.js";
import { ChecksList, ConfirmDialog, StatusChip } from "./DeliveryReview.jsx";
import { LookFiles, ViewerDialog } from "./ModelLook.jsx";
import { lookErrorText, lookedAtWhole, viewableFiles } from "./modelLook.js";
import {
  VERSION_FILTERS, VERSION_STAGE_CHIP, VERSION_STAGE_LABEL, approvableVersions, versionActionsFor, versionDecisionInput,
  versionErrorText, versionTitle,
} from "./deliveryReviewModel.js";

const REFRESH_MS = 30_000;
/** The most pages of captures one look at the list reads (300 each). */
const MAX_PAGES = 20;
const STAGE_ORDER = { review: 0, held: 1, reprocess: 2, removed: 3, approved: 4 };
/** The server's order, across pages: waiting first (warnings first, then the oldest report), decided after. */
export const sortVersionRows = (rows) => [...rows].sort((x, y) =>
  STAGE_ORDER[x.stage] - STAGE_ORDER[y.stage] || Number(y.lookFirst) - Number(x.lookFirst) ||
  (x.reportedAt ?? 0) - (y.reportedAt ?? 0) || x.sn.localeCompare(y.sn) || x.versionId.localeCompare(y.versionId));
const key = (r) => `${r.sn}/${r.versionId}`;

/** Every page of the job's list (and, for a capture with more versions than a page reads, all of that capture's). */
export async function loadVersionReview(list, jobId, fullFor = []) {
  let first = null;
  let versions = [];
  let truncated = [];
  let after = null;
  for (let i = 0; i < MAX_PAGES; i++) {
    const r = await list({ jobId, ...(after ? { after } : {}) });
    if (!first) first = r;
    if (!r?.client) return r;
    versions = versions.concat(r.versions ?? []);
    truncated = truncated.concat(r.truncated ?? []);
    if (!r.next) { after = null; break; }
    after = r.next;
  }
  for (const sn of fullFor.filter((x) => truncated.includes(x))) {
    // All of one capture's waiting versions, a page (500) at a time.
    let mine = [];
    let versionsAfter = null;
    let whole = false;
    for (let i = 0; i < MAX_PAGES; i++) {
      const r = await list({ jobId, sn, ...(versionsAfter ? { versionsAfter } : {}) });
      const seen = new Set(mine.map(key));
      mine = mine.concat((r.versions ?? []).filter((v) => !seen.has(key(v))));
      if (!r.versionsNext) { whole = true; break; }
      versionsAfter = r.versionsNext;
    }
    versions = versions.filter((v) => v.sn !== sn).concat(mine);
    if (whole) truncated = truncated.filter((x) => x !== sn);
  }
  return { ...first, versions: sortVersionRows(versions), truncated, more: after !== null };
}
const sizeOf = (bytes) => (bytes >= 1024 ** 3 ? `${(bytes / 1024 ** 3).toFixed(1)} GB` : bytes >= 1024 ** 2 ? `${(bytes / 1024 ** 2).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

/** One version's card (pure render: exported for the test). */
export function VersionCard({ row, busy, note, look, looking = false, onDecide, onAsk, onLook, onOpenFile = () => {} }) {
  const acts = versionActionsFor(row);
  // A preview is approved only once all its files could be opened here; a model's approval rests on its checks.
  const needsLook = row.previewable && !lookedAtWhole(look);
  const chip = row.lookFirst && row.stage === "review" ? ["needs", "Look first"] : [VERSION_STAGE_CHIP[row.stage], VERSION_STAGE_LABEL[row.stage]];
  return (
    <div className={`rv-card ${row.lookFirst ? "warn" : ""}`} role="group" aria-label={`${row.name}, ${versionTitle(row)}, ${chip[1]}`}>
      <div className="rv-body">
        <div className="rv-row">
          <span className="rv-name">{row.name}</span>
          <span className="rv-sn mono" title={row.sn}>…{row.sn.slice(-4)}</span>
        </div>
        <span className="rv-meta">{versionTitle(row)} · {row.files.length} {row.files.length === 1 ? "file" : "files"}, {sizeOf(row.bytes)}</span>
        <span className="rv-flag-inline"><StatusChip kind={chip[0]}>{chip[1]}</StatusChip></span>
        {row.active && <span className="rv-line">The client sees this version.</span>}
        {row.replaces && <span className="rv-line">Approving it replaces the version the client sees now.</span>}
        {row.newerActive && <span className="rv-line warn">A newer version is already shown: approving this one keeps it as an older version only.</span>}
        {row.deletionPending && <span className="rv-line warn">A deletion was asked for it: it cannot be approved.</span>}
        {row.review?.revoked && <span className="rv-line">Its approval was taken back.</span>}
        {row.review?.note && <span className="rv-line muted">Note: {row.review.note}</span>}
        <ChecksList run={{ checks: row.checks }} />
        <LookFiles look={look} onOpen={(name) => onOpenFile(row, name)} />
        <div className="rv-actions">
          {acts.approve && (
            <button type="button" className="rigv2-btn primary small rv-primary" disabled={Boolean(busy) || needsLook}
              title={needsLook ? "Look at its files first." : ""} onClick={() => onDecide(row, "approve")}>
              {busy === "approve" ? "Approving…" : "Approve"}
            </button>
          )}
          {acts.look && (
            <button type="button" className="rigv2-btn small" disabled={Boolean(busy) || looking} onClick={() => onLook(row)}
              title="Open its files in the viewer. Looking changes nothing.">
              {looking ? "Opening…" : "Look"}
            </button>
          )}
          {acts.reprocess && <button type="button" className="rigv2-btn small" disabled={Boolean(busy)} onClick={() => onDecide(row, "reprocess")}>Process again</button>}
          {acts.hold && <button type="button" className="rigv2-btn small" disabled={Boolean(busy)} onClick={() => onDecide(row, "hold")}>Hold</button>}
          {acts.remove && <button type="button" className="rigv2-btn small rv-danger" disabled={Boolean(busy)} onClick={() => onAsk(row)}>Remove</button>}
        </div>
        {note && <span className="rv-note">{note}</span>}
      </div>
    </div>
  );
}

export default function ClientVersionReview({ jobId, initial = null }) {
  const [data, setData] = useState(initial);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("review");
  const [busy, setBusy] = useState({});
  const [notes, setNotes] = useState({});
  const [looks, setLooks] = useState({});
  const [lookBusy, setLookBusy] = useState({});
  const [viewer, setViewer] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [banner, setBanner] = useState(null);
  const [full, setFull] = useState([]);

  const load = useCallback(async () => {
    if (!jobId) return;
    try {
      setData(await loadVersionReview(rigv2.listVersionReview, jobId, full));
      setError("");
    } catch (e) {
      setError(versionErrorText(e));
    }
  }, [jobId, full]);
  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  if (!data?.client) return null;
  const rows = data.versions ?? [];
  const counts = Object.fromEntries(VERSION_FILTERS.map(([id]) => [id, rows.filter((r) => r.stage === id).length]));
  const shown = rows.filter((r) => r.stage === filter);
  // A preview is approved only once its files were opened here (Look), as a guest model's thumbnail must be seen.
  const allPassed = approvableVersions(shown).filter((r) => !r.previewable || lookedAtWhole(looks[key(r)]));

  const decide = async (row, decision, note = "") => {
    setBusy((b) => ({ ...b, [key(row)]: decision }));
    setNotes((n) => ({ ...n, [key(row)]: "" }));
    try {
      await rigv2.reviewVersion(versionDecisionInput(jobId, row, decision, note));
      return { ok: true };
    } catch (e) {
      setNotes((n) => ({ ...n, [key(row)]: versionErrorText(e) }));
      return { ok: false, code: String(e?.details?.reason || e?.message || "") };
    } finally {
      setBusy((b) => ({ ...b, [key(row)]: null }));
    }
  };
  const decideAndLoad = async (row, decision, note) => {
    await decide(row, decision, note);
    await load();
  };
  /** The version's files signed again (10 minutes); with open, the viewer opens on the best file it can show. */
  const fetchLook = async (row) => {
    const r = await rigv2.lookVersion({ jobId, sn: row.sn, versionId: row.versionId });
    setLooks((l) => ({ ...l, [key(row)]: r }));
    return r;
  };
  const lookAt = async (row) => {
    setLookBusy((b) => ({ ...b, [key(row)]: true }));
    setNotes((n) => ({ ...n, [key(row)]: "" }));
    try {
      const r = await fetchLook(row);
      const first = viewableFiles(r?.files)[0];
      if (first) setViewer({ row, name: first.name });
    } catch (e) {
      setNotes((n) => ({ ...n, [key(row)]: lookErrorText(e) }));
    } finally {
      setLookBusy((b) => ({ ...b, [key(row)]: false }));
    }
  };
  const askRemove = (row) => setDialog({ kind: "remove", row });
  const approveAll = async (list) => {
    setDialog(null);
    setBanner({ kind: "info", text: `Approving ${list.length}…` });
    let ok = 0;
    let changed = 0;
    let other = 0;
    for (const row of list) {
      const r = await decide(row, "approve");
      if (r.ok) ok++;
      else if (r.code === "version_changed") changed++;
      else other++;
    }
    const parts = [`${ok} approved.`];
    if (changed) parts.push(`${changed} changed since the list loaded and ${changed === 1 ? "was" : "were"} not approved: look again.`);
    if (other) parts.push(`${other} could not be approved; each says why.`);
    setBanner({ kind: changed || other ? "error" : "info", text: parts.join(" ") });
    await load();
  };

  return (
    <div className="rigv2-review rv-client">
      <div className="rv-head">
        <div className="rv-head-left">
          <span className="rv-eyebrow">Client versions</span>
          <span className="rv-sub">
            {counts.review} {counts.review === 1 ? "version" : "versions"} to review. No client sees a version until you approve it, and each approval is for the exact version you see.
          </span>
        </div>
        <button type="button" className="rigv2-btn primary rv-primary" disabled={allPassed.length === 0 || Object.values(busy).some(Boolean)}
          onClick={() => setDialog({ kind: "approveAll", list: allPassed })}>
          Approve all that passed ({allPassed.length})
        </button>
      </div>
      <div className="rv-toolbar">
        {VERSION_FILTERS.map(([id, label]) => (
          <button key={id} type="button" className={`rigv2-chip ${filter === id ? "on" : ""}`} aria-pressed={filter === id} onClick={() => setFilter(id)}>
            {label} {counts[id]}
          </button>
        ))}
        <button type="button" className="rv-link" onClick={load}>Refresh</button>
      </div>
      {banner && <div className={`rv-banner ${banner.kind}`} role="status">{banner.text}
        <button type="button" className="rv-link" onClick={() => setBanner(null)}>Dismiss</button></div>}
      {error && <div className="rv-banner error" role="status">{error}</div>}
      {data.more && <div className="rv-banner" role="status">This job has more captures than the list reads at once: the first {MAX_PAGES * 300} are shown.</div>}
      {(data.truncated ?? []).length > 0 && (
        <div className="rv-banner" role="status">
          {data.truncated.length === 1 ? "A capture has" : `${data.truncated.length} captures have`} more versions waiting than the list shows.{" "}
          {data.truncated.map((sn) => (
            <button key={sn} type="button" className="rv-link" onClick={() => setFull((f) => (f.includes(sn) ? f : [...f, sn]))}>Show all of …{sn.slice(-4)}</button>
          ))}
        </div>
      )}
      {shown.length === 0 ? (
        <div className="rigv2-empty">{filter === "review" ? "No client versions to review." : "None here."}</div>
      ) : (
        <div className="rv-grid">
          {shown.map((row) => (
            <VersionCard key={key(row)} row={row} busy={busy[key(row)]} note={notes[key(row)]} look={looks[key(row)]}
              looking={Boolean(lookBusy[key(row)])} onDecide={decideAndLoad} onAsk={askRemove} onLook={lookAt}
              onOpenFile={(r, name) => setViewer({ row: r, name })} />
          ))}
        </div>
      )}
      {viewer && looks[key(viewer.row)] && (
        <ViewerDialog key={`${key(viewer.row)}/${viewer.name}`} title={`${viewer.row.name}, ${versionTitle(viewer.row)}`}
          approval={looks[key(viewer.row)].approval} name={viewer.name} look={looks[key(viewer.row)]}
          getLook={() => fetchLook(viewer.row)} onClose={() => setViewer(null)} />
      )}
      {dialog?.kind === "remove" && (
        <ConfirmDialog
          title={dialog.row.stage === "approved" ? "Take back this approval?" : "Remove this version?"} danger withNote confirmLabel="Remove"
          busy={Boolean(busy[key(dialog.row)])}
          lines={dialog.row.stage === "approved" ? [
            "No client can see it after this, preview links included (a link opened in the last 10 minutes may still load it until it expires).",
            "If an earlier version of it was approved, the client sees that one again.",
          ] : ["It stays unapproved and leaves the list to review. Its files are kept."]}
          onConfirm={async (note) => { const row = dialog.row; setDialog(null); await decideAndLoad(row, "remove", note); }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "approveAll" && (
        <ConfirmDialog
          title={`Approve the ${dialog.list.length} ${dialog.list.length === 1 ? "version" : "versions"} that passed every check?`}
          lines={[
            "Each approval is for the exact version listed. A version made again later needs its own approval before any client sees it.",
            "The others stay here for you.",
          ]}
          confirmLabel={`Approve ${dialog.list.length}`}
          onConfirm={() => approveAll(dialog.list)}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}
